import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '../../../../assets/icons/EditIcon.jsx'
import SaveIcon from '@mui/icons-material/Save'
import SearchIconBtn from '../../../../assets/icons/SearchIconBtn'
import {
  bulkUpdateSectionMasterApi,
  downloadSectionMasterExcel,
  searchSectionMasterApi,
  updateSectionMasterApi,
} from '../../../../api/SectionMasterApi'
import DownloadIcon from '../../../../assets/icons/DownloadIcon'
import { DEFAULT_PAGE } from '../../../../constants/index.js'
import {saveBlobAsFile} from "../../../../api/apiUtils.js"
import {sanitizeText} from "../../../../constants/index.js"
import StyledSwitch from '../../../common/StyledSwitch'
/**
 * Centralized Action Manager for handling component-specific actions
 * Provides consistent action handlers and icons across different components
 */
class ActionManager {
  constructor() {
    // Component type configurations
    this.componentConfigs = {
      sectionMaster: {
        name: 'Section Master',
        iconOrder: ['export', 'search', 'edit', 'add'],
        actions: {
          add: { message: 'Add new section', hasSpecialHandler: true },
          edit: {
            message: 'Edit functionality will be implemented here',
            severity: 'info',
            hasSpecialHandler: true,
          },
          copy: {
            message: 'Copy functionality will be implemented here',
            severity: 'info',
          },
          search: {
            message: 'Search functionality will be implemented here',
            severity: 'info',
          },
          export: {
            message: 'Exporting records',
            severity: 'success',
            hasSpecialHandler: true,
          },
          dlt: {
            message: 'Delete functionality will be implemented here',
            severity: 'warning',
          },
        },
      },
      vendorDetails: {
        name: 'Vendor Details',
        iconOrder: ['edit', 'export', 'search', 'copy', 'dlt'],
        actions: {
          edit: {
            message: 'Edit Vendor Details - Feature will be implemented',
            severity: 'info',
          },
          copy: {
            message: 'Copy Vendor Details - Feature will be implemented',
            severity: 'info',
          },
          search: {
            message: 'Search Vendor Details - Feature will be implemented',
            severity: 'info',
          },
          export: {
            message: 'Export Vendor Details - Feature will be implemented',
            severity: 'info',
          },
          dlt: {
            message: 'Delete Vendor Details - Feature will be implemented',
            severity: 'warning',
          },
        },
      },
      vendorLdcDetails: {
        name: 'Vendor LDC Details',
        iconOrder: ['monthlyToggle', 'edit', 'search', 'export', 'copy', 'dlt'],
        actions: {
          monthlyToggle: {
            message: 'Toggle monthly view',
            severity: 'info',
            hasSpecialHandler: true,
          },
          edit: {
            message: 'Edit Vendor LDC Details - Feature will be implemented',
            severity: 'info',
          },
          copy: {
            message: 'Copy Vendor LDC Details - Feature will be implemented',
            severity: 'info',
          },
          search: {
            message: 'Search Vendor LDC Details - Feature will be implemented',
            severity: 'info',
          },
          export: {
            message: 'Export Vendor LDC Details - Feature will be implemented',
            severity: 'info',
          },
          dlt: {
            message: 'Delete Vendor LDC Details - Feature will be implemented',
            severity: 'warning',
          },
        },
      },
      ruleSetting: {
        name: 'Rule Setting',
        iconOrder: ['export', 'search', 'add'],
        actions: {
          add: {
            message: 'Add Rule Setting - Feature will be implemented',
            severity: 'info',
            hasSpecialHandler: true,
          },
          edit: {
            message: 'Edit Rule Setting - Feature will be implemented',
            severity: 'info',
          },
          copy: {
            message: 'Copy Rule Setting - Feature will be implemented',
            severity: 'info',
          },
          search: {
            message: 'Search Rule Setting - Feature will be implemented',
            severity: 'info',
          },
          export: {
            message: 'Export Rule Setting - Feature will be implemented',
            severity: 'info',
          },
          dlt: {
            message: 'Delete Rule Setting - Feature will be implemented',
            severity: 'warning',
          },
        },
      },
      sectionKeyword: {
        name: 'Section Keyword',
        iconOrder: ['export', 'search', 'add'],
        actions: {
          add: { message: 'Add new section keyword', hasSpecialHandler: true },
          edit: {
            message: 'Edit functionality will be implemented here',
            severity: 'info',
            hasSpecialHandler: true,
          },
          copy: {
            message: 'Copy functionality will be implemented here',
            severity: 'info',
          },
          search: {
            message: 'Search functionality will be implemented here',
            severity: 'info',
          },
          export: {
            message: 'Exporting records',
            severity: 'success',
            hasSpecialHandler: true,
          },
          dlt: {
            message: 'Delete functionality will be implemented here',
            severity: 'warning',
          },
        },
      },
    }

    // Action icon configurations
    this.actionConfigs = {
      add: {
        icon: <AddIcon />,
        label: 'Add',
        tooltip: 'Add',
        color: 'var(--forbrand-primary)',
      },
      edit: {
        icon: <EditIcon />,
        tooltip: 'Edit',
        color: 'var(--forbrand-primary)',
      },
      copy: {
        icon: <ContentCopyIcon />,
        tooltip: 'Copy',
        color: 'var(--forbrand-primary)',
      },
      search: {
        icon: <SearchIconBtn/>,
        tooltip: 'Search',
        color: 'var(--forbrand-primary)',
      },
      dlt: {
        icon: <DeleteIcon />,
        tooltip: 'Delete',
        color: 'var(--forbrand-primary)',
      },
      export: {
        icon: <DownloadIcon />,
        toolTip: 'Download Excel',
        color: 'var(--forbrand-primary)',
      },
      monthlyToggle: {
        icon: null, // Rendered dynamically as StyledSwitch
        tooltip: 'Monthly View',
        color: 'var(--forbrand-primary)',
        isToggle: true,
      },
    }

    // Generate action handlers dynamically
    this.actionHandlers = this.generateActionHandlers()
  }

  /**
   * Generate action handlers dynamically based on component configurations
   */
  generateActionHandlers() {
    const handlers = {}

    Object.keys(this.componentConfigs).forEach(componentType => {
      handlers[componentType] = {}
      const config = this.componentConfigs[componentType]

      Object.keys(config.actions).forEach(actionType => {
        const handlerName = `handle${this.capitalize(
          componentType
        )}${this.capitalize(actionType)}`
        handlers[componentType][actionType] =
          this[handlerName] ||
          this.createGenericHandler(componentType, actionType)
      })
    })

    return handlers
  }

  /**
   * Create a generic handler for actions that don't have special implementations
   */
  createGenericHandler(componentType, actionType) {
    return (data, context) => {
      const config = this.componentConfigs[componentType]
      const actionConfig = config.actions[actionType]

      this.showNotification(
        context,
        actionConfig.message,
        actionConfig.severity || 'info'
      )
    }
  }

  /**
   * Show notification using context or fallback to alert
   */
  showNotification(context, message, severity = 'info') {
    if (context?.setNotification) {
      context.setNotification({
        open: true,
        message,
        severity,
      })
    } else {
      alert(message)
    }
  }

  /**
   * Capitalize first letter of a string
   */
  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  // Special handlers that need custom logic
  handleSectionMasterAdd = (data, context) => {
    if (context?.showAddSection) {
      context.showAddSection(true)
    } else {
      this.showNotification(context, 'Add new section functionality', 'info')
    }
  }

  handleSectionMasterExport = async (data, context) => {
    // Check if there's no data to download
    const tableData = context?.tableData || []
    if (tableData.length === 0) {
      this.showNotification(context, 'No Data Found', 'info')
      return
    }
    
    try {
      // Use the same payload as searchSectionMasterApi
      const payload = {
        pageFilter: {
          currentPage: 1,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'sectionCode',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: [],
      }
      // Get tanUuid from context (from SectionMaster state)
      const tanUuid = context?.selectedTanMasterUuid || ''
      if (!tanUuid) {
        this.showNotification(
          context,
          'Please select a TAN before exporting.',
          'warning'
        )
        return
      }
      const blob = await downloadSectionMasterExcel(payload, tanUuid)
      // Trigger file download
      const url = window.URL.createObjectURL(blob)
      
      // Secure download handler to avoid XSS (Veracode-safe)
        if (url && blob instanceof Blob) {
          saveBlobAsFile(blob, 'SectionMaster.xlsx');
        } else {
          throw new Error('Invalid file data received.');
        }
    } catch (error) {
      this.showNotification(
        context,
        error.message || 'Failed to download Section Master Excel',
        'error'
      )
    }
  }

  handleSectionMasterDlt = (data, context) => {
    const selectedCount = context?.selectedRows?.length || 0

    if (selectedCount === 0) {
      this.showNotification(
        context,
        'Please select at least one row to delete',
        'warning'
      )
      return
    }

    // In a real application, you would show a confirmation dialog here
    // For now, we'll just show a success message
    this.showNotification(
      context,
      `${selectedCount} section(s) deleted successfully`,
      'success'
    )

    // Clear selected rows after deletion
    if (context?.setSelectedRows) {
      context.setSelectedRows([])
    }
  }

  handleSectionMasterEdit = async (rowData, context) => {
    const { selectedRows = [], setEditingRowId } = context || {}
    // If already editing, save the changes (single-row flow)
    if (context?.editingRowId !== null) {
      await this.handleSectionMasterSaveEdit(rowData, context)
      return
    }

    // If one row is selected, enable single-row edit
    if (selectedRows.length === 1) {
      if (setEditingRowId) setEditingRowId(selectedRows[0])
      this.showNotification(
        context,
        'Edit mode enabled for the selected row. Click Save to apply changes.',
        'info'
      )
      return
    }

    // If clicked on a row's Edit icon (rowData provided), enable edit for that row
    if (rowData && rowData.id !== undefined) {
      if (setEditingRowId) setEditingRowId(rowData.id)
      this.showNotification(
        context,
        'Edit mode enabled for the selected row. Click Save to apply changes.',
        'info'
      )
      return
    }

    // If multiple rows are selected, block and ask user to select only one for single edit
    if (selectedRows.length > 1) {
      this.showNotification(
        context,
        'Select exactly one row to edit, or deselect all to bulk update.',
        'warning'
      )
      return
    }

    // No selection and no specific row -> enter table-wide edit mode
    if (setEditingRowId) setEditingRowId('__ALL__')
    this.showNotification(
      context,
      'Edit mode enabled for all rows. Click Save to apply all changes.',
      'info'
    )
  }

  // Build payload object for API from table row and raw flags
  buildSectionPayloadFromRow(row, rawList, tanUuid) {
    const toDD = s => {
      const t = (s || '').toString().trim()
      if (!t) return ''
      if (/^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(t)) return t.replace(/-/g, '/')
      if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
        const [y, m, d] = t.split('-')
        return `${d}/${m}/${y}`
      }
      return t
    }
    const uuid = row.sectionMasterUuid || row.sectionMasterUuid
    const match = (rawList || []).find(it => (it.uuid || it.id) === uuid) || {}
    return {
      uuid, // use editing row id/uuid
      sectionCode: row.section || '',
      broadHead: row.broadHead || '',
      startDate: toDD(row.startDate) || '',
      endDate: row.endDate ? toDD(row.endDate) : '',
      applicableTo: row.applicableTo || '',
      payeeStatus: row.payeeStatus || '',
      thresholdAnnual: row.thresholdAnnual || 0,
      thresholdTransaction: row.thresholdTransaction || 0,
      prescribedRate: row.prescribedRate || 0,
      globalSectionMaster:row.globalSectionMaster,
      associateSectionMaster:row?.associateSectionMaster,
      tanUuid: tanUuid || '',
    }
  }

  // Helper: parse date in multiple accepted formats
  parseDate(s) {
    if (!s) return null
    const t = String(s).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
      const [y, m, d] = t.split('-').map(Number)
      return new Date(y, m - 1, d)
    }
    if (/^\d{2}[\/. -]\d{2}[\/. -]\d{4}$/.test(t)) {
      const parts = t.split(/[\/. -]/).map(Number)
      const [d, m, y] = parts
      return new Date(y, m - 1, d)
    }
    const dt = new Date(t)
    return isNaN(dt.getTime()) ? null : dt
  }

  // Detect gap between previous End Date and new Start Date for same Section & Broad Head
  detectGapForRow(row, rawList = []) {
    const secVal = String(row.section || '')
      .trim()
      .toLowerCase()
    const bhVal = String(row.broadHead || '')
      .trim()
      .toLowerCase()
    const newStart = this.parseDate(row.startDate)
    if (!secVal || !bhVal || !newStart) return false
    const candidates = (Array.isArray(rawList) ? rawList : []).filter(it => {
      const oid = it.uuid || it.id
      if (oid === (row.sectionMasterUuid || row.sectionMasterUuid)) return false
      const sec = String(it.section ?? it.sectionCode ?? '')
        .trim()
        .toLowerCase()
      const bh = String(it.broadHead ?? '')
        .trim()
        .toLowerCase()
      return sec === secVal && bh === bhVal
    })
    const prevEnds = candidates
      .map(it => this.parseDate(it.endDate))
      .filter(d => d instanceof Date && !isNaN(d))
    if (prevEnds.length === 0) return false
    const before = prevEnds.filter(d => d < newStart).sort((a, b) => b - a)
    if (before.length === 0) return false
    const prevEnd = before[0]
    const expected = new Date(
      prevEnd.getFullYear(),
      prevEnd.getMonth(),
      prevEnd.getDate() + 1
    )
    const sameDay =
      newStart.getFullYear() === expected.getFullYear() &&
      newStart.getMonth() === expected.getMonth() &&
      newStart.getDate() === expected.getDate()
    return !sameDay
  }

  // Internal: perform single update
  async _saveSectionMaster(rowData, context, payloadOverride) {
    context.setLoading(true)
    const { selectedTanMasterUuid: tanUuid = '' } = context || {}
    const rawList = context?.rawSectionMasterList || []
    const toDD = context?.toDD || (s => s)
    const uuid = rowData.id || rowData.sectionMasterUuid
    const match =
      (rawList || []).find(r => (r.uuid || r.sectionMasterUuid) === uuid) || {}
    const globalSectionMaster = !!match.globalSectionMaster
    const associateSectionMaster = !!match.associateSectionMaster
    const payload = payloadOverride || {
      sectionCode: rowData.section || '',
      broadHead: rowData.broadHead || '',
      startDate: toDD(rowData.startDate) || '',
      endDate: rowData.endDate ? toDD(rowData.endDate) : '',
      applicableTo: rowData.applicableTo || '',
      payeeStatus: rowData.payeeStatus || '',
      thresholdAnnual: rowData.thresholdAnnual || 0 ,
      thresholdTransaction: rowData.thresholdTransaction || 0,
      prescribedRate: rowData.prescribedRate || 0,
      globalSectionMaster,
      associateSectionMaster,
      tanUuid,
    }
    try {
      const res = await updateSectionMasterApi(payload, uuid)
      // If API itself returned an error-like response, throw it
      if (!res || res.status >= 400 || res.success === false) {
        throw new Error(res?.data?.message || 'API update failed')
      }
      return res
    } catch (e) {
      // Ensure caller gets a proper error
      throw new Error(
        e?.response?.data?.message || e?.message || 'API update failed'
      )
    } finally {
      context.setLoading(false)
    }
  }

  // Internal: perform bulk update with refresh & state clear
  async _doBulkUpdate(candidateRows, context) {
    context.setLoading(true)
    try {
      const tanUuid = context?.selectedTanMasterUuid || ''
      const rows = context?.tableData || []
      const rawList = context?.rawSectionMasterList || []
      const editingData = context?.editingData || {}
      const payloadArray = candidateRows.map(row => {
        const idKey = String(row.sectionMasterUuid)
        const merged = {
          ...row,
          ...(editingData[idKey] || editingData[row.sectionMasterUuid] || {}),
        }
        return this.buildSectionPayloadFromRow(merged, rawList, tanUuid)
      })

      const res = await bulkUpdateSectionMasterApi(payloadArray)
      if (res && res.success === false)
        throw new Error(res.error || 'Failed to bulk update sections')
      const searchRes = await searchSectionMasterApi(
        {
          pageFilter: {
            currentPage: 1,
            includeCount: true,
            pageSize: 100,
            sortField: 'sectionCode',
            sortOrder: 'ASC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: [],
          hitType: 'GET_SECTION_MASTER',
        },
        tanUuid
      )
      const apiData = searchRes?.data
      const list =
        apiData?.data?.list ||
        apiData?.list ||
        apiData?.data?.content ||
        apiData?.content ||
        []
      context.setRawSectionMasterList(list)
      const mapRows = items =>
        Array.isArray(items)
          ? items.map((created, i) => {
              const mapped = {
                section: created.section ?? created.sectionCode ?? '',
                broadHead: created.broadHead ?? '',
                startDate: created.startDate ? created.startDate : '',
                endDate: created.endDate ? created.endDate : '',
                applicableTo: created.applicableTo ?? '',
                payeeStatus: created.payeeStatus ?? '',
                thresholdAnnual: created.thresholdAnnual ?? '',
                thresholdTransaction: created.thresholdTransaction ?? '',
                prescribedRate: created.prescribedRate ?? '',
                sectionMasterUuid: created.uuid, // make sure every row has an id
                globalSectionMaster:created.globalSectionMaster,
                associateSectionMaster:created?.associateSectionMaster,
              }

              return mapped
            })
          : []
      if (context?.setTableData) context.setTableData(mapRows(list))
      if (context?.setRawSectionMasterList)
        context.setRawSectionMasterList(mapRows(list))

      // Clear edit state after successful update
      if (context?.setEditingRowId) context.setEditingRowId(null)
      if (context?.setEditingData) context.setEditingData({})
      if (context?.setValidationErrors) context.setValidationErrors({})
      if (context?.setSelectedRows) context.setSelectedRows([])
      this.showNotification(context, 'Bulk update successful!', 'success')
    } catch (error) {
      if (context?.setEditingRowId) context.setEditingRowId(null)
      if (context?.setEditingData) context.setEditingData({})
      if (context?.setValidationErrors) context.setValidationErrors({})
      if (context?.setSelectedRows) context.setSelectedRows([])

      try {
        const tanUuid = context?.selectedTanMasterUuid || ''
        const searchRes = await searchSectionMasterApi(
          {
            pageFilter: {
              currentPage: 1,
              includeCount: true,
              pageSize: 100,
              sortField: 'sectionCode',
              sortOrder: 'ASC',
            },
            requestFilter: [],
            isReportDownload: false,
            searchCOList: [],
            hitType: 'GET_SECTION_MASTER',
          },
          tanUuid
        )
        const apiData = searchRes?.data
        const list =
          apiData?.data?.list ||
          apiData?.list ||
          apiData?.data?.content ||
          apiData?.content ||
          []
        const mapRows = items =>
          Array.isArray(items)
            ? items.map((created, i) => {
                const mapped = {
                  section: created.section ?? created.sectionCode ?? '',
                  broadHead: created.broadHead ?? '',
                  startDate: created.startDate ? created.startDate : '',
                  endDate: created.endDate ? created.endDate : '',
                  applicableTo: created.applicableTo ?? '',
                  payeeStatus: created.payeeStatus ?? '',
                  thresholdAnnual: created.thresholdAnnual ?? '',
                  thresholdTransaction: created.thresholdTransaction ?? '',
                  prescribedRate: created.prescribedRate ?? '',
                  sectionMasterUuid: created.uuid ?? i, // make sure every row has an id
                }

                return mapped
              })
            : []
        if (context?.setTableData) context.setTableData(mapRows(list))
        if (context?.setRawSectionMasterList)
          context.setRawSectionMasterList(mapRows(list))
      } catch (refreshError) {
        this.showNotification(
          context,
          'Failed to refresh data after bulk update error',
          'error'
        )
      }
      throw error // still rethrow so caller knows
    } finally {
      context.setLoading(false)
    }
  }

  // Bulk update handler: called when Edit icon is clicked with no selection
  // handleSectionMasterBulkUpdate = async (context) => {
  //     try {
  //         const tanUuid = context?.selectedTanMasterUuid || '';
  //         if (!tanUuid) {
  //             this.showNotification(context, 'Please select a TAN before bulk update.', 'warning');
  //             return;
  //         }
  //         const rows = context?.tableData || [];
  //         if (!Array.isArray(rows) || rows.length === 0) {
  //             this.showNotification(context, 'No data available to update.', 'warning');
  //             return;
  //         }
  //         const rawList = context?.rawSectionMasterList || [];

  //         // Validations per row (same rules as single edit)
  //         const parseDate = (s) => {
  //             if (!s) return null;
  //             const t = String(s).trim();
  //             if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
  //                 const [y, m, d] = t.split('-').map(Number);
  //                 return new Date(y, m - 1, d);
  //             }
  //             if (/^\d{2}[\/. -]\d{2}[\/. -]\d{4}$/.test(t)) {
  //                 const parts = t.split(/[\/. -]/).map(Number);
  //                 const [d, m, y] = parts;
  //                 return new Date(y, m - 1, d);
  //             }
  //             const dt = new Date(t);
  //             return isNaN(dt.getTime()) ? null : dt;
  //         };
  //         const today = new Date(); today.setHours(0, 0, 0, 0);
  //         const isEmpty = (v) => v === null || v === undefined || String(v).trim() === '' || String(v).trim() === '-';
  //         const num18_2 = /^\d{1,18}(\.\d{1,2})?$/;
  //         const num3_2 = /^\d{1,3}(\.\d{1,2})?$/;

  //         // Only validate and submit rows that actually changed (if editingData map is provided)
  //         const editingData = context?.editingData || {};
  //         const changedRowIds = new Set(Object.keys(editingData).map(k => isNaN(Number(k)) ? k : Number(k)));
  //         const candidateRows = rows.filter(r => changedRowIds.has(String(r.sectionMasterUuid)) || changedRowIds.has(r.sectionMasterUuid));

  //         const errors = [];
  //         const gapRows = [];
  //         candidateRows.forEach((row) => {
  //             const globalIdx = rows.indexOf(row);
  //             const id = row.id || row.sectionMasterUuid;
  //             const merged = { ...row, ...(editingData[id] || editingData[String(id)] || {}) };

  //             const start = parseDate(merged.startDate);
  //             const end = merged.endDate ? parseDate(merged.endDate) : null;

  //             // 1. Overlap against existing data for same section + broadHead
  //             const secVal = String(merged.section || '').trim().toLowerCase();
  //             const bhVal = String(merged.broadHead || '').trim().toLowerCase();
  //             if (secVal && bhVal && start) {
  //                 const candidates = (Array.isArray(rawList) ? rawList : []).filter((it) => {
  //                     // Get all possible IDs from the raw data item
  //                     const rawId = it.uuid || it.id || it.sectionMasterUuid;
  //                     // Get all possible IDs from the current row being edited
  //                     const currentRowId = merged.id || merged.sectionMasterUuid || merged.uuid || id;

  //                     // Exclude current row being edited - comprehensive ID matching
  //                     if (rawId && currentRowId) {
  //                         if (rawId === currentRowId ||
  //                             String(rawId) === String(currentRowId) ||
  //                             rawId === id ||
  //                             String(rawId) === String(id)) {
  //                             return false;
  //                         }
  //                     }

  //                     // Only include rows with the same section and broadhead
  //                     const sec = String(it.section ?? it.sectionCode ?? '').trim().toLowerCase();
  //                     const bh = String(it.broadHead ?? '').trim().toLowerCase();
  //                     return sec === secVal && bh === bhVal;
  //                 });
  //                 const newStart = start;
  //                 const newEnd = end || new Date(9999, 11, 31);
  //                 const overlap = candidates.some((it) => {
  //                     const exStart = parseDate(it.startDate);
  //                     const exEnd = it.endDate ? parseDate(it.endDate) : new Date(9999, 11, 31);
  //                     if (!exStart) return false;
  //                     return newStart <= exEnd && exStart <= newEnd;
  //                 });
  //                 if (overlap) {
  //                     errors.push({ idx: globalIdx, reason: 'Overlapping date range with existing record' });
  //                 }
  //                 // Detect gap and collect to confirm
  //                 const prevEnds = candidates.map(it => parseDate(it.endDate)).filter(d => d instanceof Date && !isNaN(d));
  //                 if (prevEnds.length > 0) {
  //                     const before = prevEnds.filter(d => d < newStart).sort((a, b) => b - a);
  //                     if (before.length > 0) {
  //                         const prevEnd = before[0];
  //                         const expected = new Date(prevEnd.getFullYear(), prevEnd.getMonth(), prevEnd.getDate() + 1);
  //                         const sameDay = newStart.getFullYear() === expected.getFullYear() && newStart.getMonth() === expected.getMonth() && newStart.getDate() === expected.getDate();
  //                         if (!sameDay) gapRows.push(merged);
  //                     }
  //                 }
  //             }

  //             // 2. End before start
  //             if (start && end && end < start) errors.push({ idx: globalIdx, reason: 'End Date before Start Date' });
  //             // 3. Same start and end
  //             if (start && end && end.getTime() === start.getTime()) errors.push({ idx: globalIdx, reason: 'Start Date and End Date cannot be the same' });

  //             // 5-7. Numeric validations
  //             if (!isEmpty(merged.thresholdAnnual) && !num18_2.test(String(merged.thresholdAnnual).trim())) {
  //                 errors.push({ idx: globalIdx, reason: 'Threshold Annual invalid (max 18 digits and 2 decimals)' });
  //             }
  //             if (!isEmpty(merged.thresholdTransaction) && !num18_2.test(String(merged.thresholdTransaction).trim())) {
  //                 errors.push({ idx: globalIdx, reason: 'Threshold Per Transaction invalid (max 18 digits and 2 decimals)' });
  //             }
  //             if (!isEmpty(merged.prescribedRate) && !num3_2.test(String(merged.prescribedRate).trim())) {
  //                 errors.push({ idx: globalIdx, reason: 'Prescribed Rate invalid (max 3 digits and 2 decimals)' });
  //             }
  //         });

  //         // Stop for validation errors
  //         if (errors.length > 0) {
  //             if (typeof context?.setValidationErrors === 'function') {
  //                 const validationMap = {};
  //                 errors.forEach((e) => {
  //                     const r = rows[e.idx] || {};
  //                     if (r.sectionMasterUuid !== undefined && r.sectionMasterUuid !== null) {
  //                         validationMap[`${r.sectionMasterUuid}_section`] = e.reason;
  //                     }
  //                 });
  //                 context.setValidationErrors(validationMap);
  //             }
  //             const summarized = errors.slice(0, 5).map((e) => {
  //                 const r = rows[e.idx] || {};
  //                 const label = `#${e.idx + 1} [${String(r.section || '')} / ${String(r.broadHead || '')}] ${String(r.startDate || '')}`;
  //                 return `${label}: ${e.reason}`;
  //             });
  //             const more = errors.length > 5 ? ` and ${errors.length - 5} more` : '';
  //             this.showNotification(context, `Bulk update blocked. ${errors.length} row(s) invalid: ${summarized.join('; ')}${more}`, 'error');
  //             return;
  //         }

  //         // If any gap rows found, confirm once for the operation
  //         if (gapRows.length > 0 && typeof context?.openGapConfirm === 'function') {
  //             context.openGapConfirm(
  //                 'There is a gap between the "End Date" & "Start Date" for which there will be no applicability of section & broadhead. Do you want to continue?',
  //                 async () => {
  //                     await this._doBulkUpdate(candidateRows, context);
  //                 }
  //             );
  //             return; // wait for confirmation
  //         }

  //         // Proceed without gaps or confirmation not available
  //         await this._doBulkUpdate(candidateRows, context);

  //         if (errors.length > 0) {
  //             // Set row-level validation markers for UI highlighting
  //             if (typeof context?.setValidationErrors === 'function') {
  //                 const validationMap = {};
  //                 errors.forEach((e) => {
  //                     const r = rows[e.idx] || {};
  //                     if (r.sectionMasterUuid !== undefined && r.sectionMasterUuid !== null) {
  //                         validationMap[`${r.sectionMasterUuid}_section`] = e.reason;
  //                     }
  //                 });
  //                 context.setValidationErrors(validationMap);
  //             }
  //             const summarized = errors.slice(0, 5).map((e) => {
  //                 const r = rows[e.idx] || {};
  //                 const label = `#${e.idx + 1} [${String(r.section || '')} / ${String(r.broadHead || '')}] ${String(r.startDate || '')}`;
  //                 return `${label}: ${e.reason}`;
  //             });
  //             const more = errors.length > 5 ? ` and ${errors.length - 5} more` : '';
  //             this.showNotification(context, `Bulk update blocked. ${errors.length} row(s) invalid: ${summarized.join('; ')}${more}`, 'error');
  //             return;
  //         }

  //         // Build payloads only for changed rows and submit
  //         const payloadArray = candidateRows.map((row) => {
  //             const idKey = String(row.sectionMasterUuid);
  //             const merged = { ...row, ...(editingData[idKey] || editingData[row.sectionMasterUuid] || {}) };
  //             // Gap confirmation per row before building payload
  //             const secVal = String(merged.section || '').trim().toLowerCase();
  //             const bhVal = String(merged.broadHead || '').trim().toLowerCase();
  //             const parseDate = (s) => {
  //                 if (!s) return null;
  //                 const t = String(s).trim();
  //                 if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
  //                     const [y, m, d] = t.split('-').map(Number);
  //                     return new Date(y, m - 1, d);
  //                 }
  //                 if (/^\d{2}[\/. -]\d{2}[\/. -]\d{4}$/.test(t)) {
  //                     const parts = t.split(/[\/. -]/).map(Number);
  //                     const [d, m, y] = parts;
  //                     return new Date(y, m - 1, d);
  //                 }
  //                 const dt = new Date(t);
  //                 return isNaN(dt.getTime()) ? null : dt;
  //             };
  //             const newStart = parseDate(merged.startDate);
  //             const end = merged.endDate ? parseDate(merged.endDate) : null;
  //             if (secVal && bhVal && newStart) {
  //                 const candidates = (Array.isArray(rawList) ? rawList : []).filter((it) => {
  //                     const oid = it.uuid || it.id;
  //                     // Exclude current row - check multiple ID formats
  //                     if (oid === merged.id || oid === merged.sectionMasterUuid ||
  //                         String(oid) === String(merged.id) || String(oid) === String(merged.sectionMasterUuid)) return false;
  //                     const sec = String(it.section ?? it.sectionCode ?? '').trim().toLowerCase();
  //                     const bh = String(it.broadHead ?? '').trim().toLowerCase();
  //                     return sec === secVal && bh === bhVal;
  //                 });
  //                 const prevEnds = candidates.map(it => parseDate(it.endDate)).filter(d => d instanceof Date && !isNaN(d));
  //                 if (prevEnds.length > 0) {
  //                     const before = prevEnds.filter(d => d < newStart).sort((a, b) => b - a);
  //                     if (before.length > 0) {
  //                         const prevEnd = before[0];
  //                         const expected = new Date(prevEnd.getFullYear(), prevEnd.getMonth(), prevEnd.getDate() + 1);
  //                         const sameDay = newStart.getFullYear() === expected.getFullYear() && newStart.getMonth() === expected.getMonth() && newStart.getDate() === expected.getDate();
  //                         if (!sameDay && typeof context?.openGapConfirm === 'function') {
  //                             // Queue confirmation; actual save will be triggered by ActionManager at callsite
  //                             context.openGapConfirm(
  //                                 'There is a gap between the "End Date" & "Start Date" for which there will be no applicability of section & broadhead. Do you want to continue?',
  //                                 async () => { }
  //                             );
  //                         }
  //                     }
  //                 }
  //             }
  //             return this.buildSectionPayloadFromRow(merged, rawList, tanUuid);
  //         });

  //         // Use the already-fixed _doBulkUpdate method instead of duplicate code
  //         await this._doBulkUpdate(candidateRows, context);
  //     } catch (e) {
  //         this.showNotification(context, e.message || 'Failed to bulk update sections', 'error');
  //         // Clear editing state on error
  //         if (context?.setEditingRowId) context.setEditingRowId(null);
  //         if (context?.setEditingData) context.setEditingData({});
  //         if (context?.setValidationErrors) context.setValidationErrors({});
  //         if (context?.setSelectedRows) context.setSelectedRows([]);
  //     }
  // };

  /** ---------------- Common Row Validation ---------------- */

  validateSectionMasterRow = (rowData, rawList) => {
    const errors = []

    const parseDate = s => {
      if (!s) return null
      const t = String(s).trim()
      if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
        const [y, m, d] = t.split('-').map(Number)
        return new Date(y, m - 1, d)
      }
      if (/^\d{2}[\/. -]\d{2}[\/. -]\d{4}$/.test(t)) {
        const parts = t.split(/[\/. -]/).map(Number)
        const [d, m, y] = parts
        return new Date(y, m - 1, d)
      }
      const dt = new Date(t)
      return isNaN(dt.getTime()) ? null : dt
    }

    const isEmpty = val =>
      val === null || val === undefined || String(val).trim() === ''

    const start = parseDate(rowData.startDate)
    const end = rowData.endDate ? parseDate(rowData.endDate) : null

    // 🔹 Start Date required
    if (!start) errors.push('Start Date is required and must be valid.')

    // 🔹 End Date rules
    if (end) {
      if (end <= start) errors.push('End Date must be greater than Start Date.')
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (end <= today) errors.push('End Date should only be in future dates.')
    }

    // 🔹 Numeric Validations
    const num18_2 = /^\d{1,18}(\.\d{1,2})?$/
    const num3_2 = /^\d{1,3}(\.\d{1,2})?$/


    if (isEmpty(rowData.payeeStatus)) {
        errors.push('Payee status is required.')
    }

    if (isEmpty(rowData.thresholdAnnual))
      errors.push('Threshold Annual cannot be empty.')
    else if (!num18_2.test(String(rowData.thresholdAnnual).trim()))
      errors.push('Threshold Annual must be up to 18 digits with 2 decimals.')

    if (isEmpty(rowData.thresholdTransaction))
      errors.push('Threshold Transaction cannot be empty.')
    else if (!num18_2.test(String(rowData.thresholdTransaction).trim()))
      errors.push(
        'Threshold Transaction must be up to 18 digits with 2 decimals.'
      )

    if (isEmpty(rowData.prescribedRate))
      errors.push('Prescribed Rate cannot be empty.')
    else if (!num3_2.test(String(rowData.prescribedRate).trim()))
      errors.push('Prescribed Rate must be up to 3 digits with 2 decimals.')

    // 🔹 Broad Head Validation
    const broadHead = String(rowData.broadHead || '').trim()
    // if (broadHead && !/^[a-zA-Z0-9\s]{1,1000}$/.test(broadHead)) {
    //   errors.push(
    //     'Broad Head can only be alphanumeric (no special characters, max 1000 chars).'
    //   )
    // }

    // 🔹 Overlap Validation
    if (start) {
      const secVal = String(rowData.section || '')
        .trim()
        .toLowerCase()
      const bhVal = broadHead.toLowerCase()

      const candidates = (Array.isArray(rawList) ? rawList : []).filter(it => {
        const rawId = it.sectionMasterUuid || it.uuid || it.id
        const currentId = rowData.sectionMasterUuid
        if (rawId && currentId && String(rawId) === String(currentId))
          return false
        const sec = String(it.section ?? it.sectionCode ?? '')
          .trim()
          .toLowerCase()
        const bh = String(it.broadHead ?? '')
          .trim()
          .toLowerCase()
        return sec === secVal && bh === bhVal
      })

      const newStart = start
      const newEnd = end || new Date(9999, 11, 31)

      // Overlap check
      const overlap = candidates.some(it => {
        const exStart = parseDate(it.startDate)
        const exEnd = it.endDate
          ? parseDate(it.endDate)
          : new Date(9999, 11, 31)
        if (!exStart) return false
        return newStart <= exEnd && exStart <= newEnd
      })
      if (overlap) errors.push('Overlapping date range with existing record.')

      // Gap detection
      const prevEnds = candidates
        .map(it => parseDate(it.endDate))
        .filter(d => d instanceof Date && !isNaN(d))
      if (prevEnds.length > 0) {
        const before = prevEnds.filter(d => d < newStart).sort((a, b) => b - a)
        if (before.length > 0) {
          const prevEnd = before[0]
          const expected = new Date(
            prevEnd.getFullYear(),
            prevEnd.getMonth(),
            prevEnd.getDate() + 1
          )
          const sameDay =
            newStart.getFullYear() === expected.getFullYear() &&
            newStart.getMonth() === expected.getMonth() &&
            newStart.getDate() === expected.getDate()
          if (!sameDay)
            errors.push(
              'Gap detected between previous End Date and current Start Date.'
            )
        }
      }
    }

    return errors
  }

  /** ---------------- Bulk Update ---------------- */
  handleSectionMasterBulkUpdate = async context => {
    try {
      const rawList = context?.rawSectionMasterList || []
      const rows = Array.isArray(context?.tableData) ? context.tableData : []

      if (rows.length === 0) {
        this.showNotification(
          context,
          'No data available to update.',
          'warning'
        )
        return
      }

      const editingData = context?.editingData || {}
      const changedRowIds = new Set(Object.keys(editingData))
      const candidateRows = rows.filter(r =>
        changedRowIds.has(String(r.sectionMasterUuid))
      )

      if (candidateRows.length === 0) {
        this.showNotification(context, 'No changes detected to update.', 'info')
        return
      }

      // Validate each candidate row one by one
      for (let i = 0; i < candidateRows.length; i++) {
        const rowData = candidateRows[i]
        const merged = {
          ...rowData,
          ...(editingData[rowData.sectionMasterUuid] || {}),
        }
        const rowErrors = this.validateSectionMasterRow(merged, rawList)

        if (rowErrors.length > 0) {
          // Show only first row errors
          const msg = rowErrors
            .map(
              reason =>
                `#${rows.indexOf(rowData) + 1} [${merged.section} / ${
                  merged.broadHead
                }] → ${reason}`
            )
            .join('\n')
          this.showNotification(
            context,
            `Bulk update blocked.\n${msg}`,
            'error'
          )
          return // Stop here, wait for user to fix this row
        }
      }

      // ✅ All rows valid → proceed with bulk update API
      await this._doBulkUpdate(candidateRows, context)

      // 🔹 Reload table data after successful update
      const tanUuid = context?.selectedTanMasterUuid || ''
      if (tanUuid) {
        const searchRes = await searchSectionMasterApi(
          {
            pageFilter: {
              currentPage: 1,
              includeCount: true,
              pageSize: 100,
              sortField: 'sectionCode',
              sortOrder: 'ASC',
            },
            requestFilter: [],
            isReportDownload: false,
            searchCOList: [],
            hitType: 'GET_SECTION_MASTER',
          },
          tanUuid
        )

        const apiData = searchRes?.data
        const list =
          apiData?.data?.list ||
          apiData?.list ||
          apiData?.data?.content ||
          apiData?.content ||
          []

        const mapRows = items =>
          Array.isArray(items)
            ? items.map(created => ({
                section: created.section ?? created.sectionCode ?? '',
                broadHead: created.broadHead ?? '',
                startDate: created.startDate ? created.startDate : '',
                endDate: created.endDate ? created.endDate : '',
                applicableTo: created.applicableTo ?? '',
                payeeStatus: created.payeeStatus ?? '',
                thresholdAnnual: created.thresholdAnnual ?? '',
                thresholdTransaction: created.thresholdTransaction ?? '',
                prescribedRate: created.prescribedRate ?? '',
                sectionMasterUuid: created.uuid,
                globalSectionMaster:created.globalSectionMaster,
                associateSectionMaster: created.associateSectionMaster,
              }))
            : []

        if (context?.setTableData) context.setTableData(mapRows(list))
        if (context?.setRawSectionMasterList) context.setRawSectionMasterList(mapRows(list))
        if (context?.setSelectedRows) context.setSelectedRows([])
        if (context?.setEditingData) context.setEditingData({})
        if (context?.setEditingRowId) context.setEditingRowId(null)
        if (context?.setValidationErrors) context.setValidationErrors({})
      }

      this.showNotification(context, 'Bulk update successful!', 'success')
    } catch (e) {
      this.showNotification(context, e.message || 'Bulk update failed', 'error')
    }
  }

  // handleSectionMasterSaveEdit = async (rowData, context) => {
  //     console.log(rowData,context,"context");

  //     try {
  //         const rawList = context?.rawSectionMasterList || [];
  //         const uuid = rowData.id || rowData.sectionMasterUuid;
  //         const match = rawList.find(row => row.uuid === uuid);
  //         const globalSectionMaster = match ? match.globalSectionMaster : false;
  //         const associateSectionMaster = match ? match.associateSectionMaster : false;
  //         const sectionMasterUuid = rowData.id || match?.uuid || '';
  //         const tanUuid = context?.selectedTanMasterUuid || '';

  //         // Helpers
  //         const parseDate = (s) => {
  //             if (!s) return null;
  //             const t = String(s).trim();
  //             if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
  //                 const [y, m, d] = t.split('-').map(Number);
  //                 return new Date(y, m - 1, d);
  //             }
  //             if (/^\d{2}[\/. -]\d{2}[\/. -]\d{4}$/.test(t)) {
  //                 const parts = t.split(/[\/. -]/).map(Number);
  //                 const [d, m, y] = parts;
  //                 return new Date(y, m - 1, d);
  //             }
  //             const dt = new Date(t);
  //             return isNaN(dt.getTime()) ? null : dt;
  //         };
  //         const toDD = (s) => {
  //             const t = (s || '').toString().trim();
  //             if (!t || t === '-') return '';
  //             if (/^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(t)) return t.replace(/-/g, '/');
  //             if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
  //                 const [y, m, d] = t.split('-');
  //                 return `${d}/${m}/${y}`;
  //             }
  //             return t;
  //         };

  //         // Dates
  //         const start = parseDate(rowData.startDate);
  //         const end = rowData.endDate ? parseDate(rowData.endDate) : null;
  //         const today = new Date(); today.setHours(0, 0, 0, 0);

  //         // 1. Overlap validation (same Section + Broad Head)
  //         const secVal = String(rowData.section || '').trim().toLowerCase();
  //         const bhVal = String(rowData.broadHead || '').trim().toLowerCase();
  //         if (secVal && bhVal && start) {
  //             const candidates = (Array.isArray(rawList) ? rawList : []).filter((it) => {
  //                 // Get all possible IDs from the raw data item
  //                 const rawId = it.uuid || it.id || it.sectionMasterUuid;
  //                 // Get all possible IDs from the current row being edited
  //                 const currentRowId = rowData.id || rowData.sectionMasterUuid || rowData.uuid || sectionMasterUuid;

  //                 // Exclude current row being edited - comprehensive ID matching
  //                 if (rawId && currentRowId) {
  //                     if (rawId === currentRowId ||
  //                         String(rawId) === String(currentRowId) ||
  //                         rawId === sectionMasterUuid ||
  //                         String(rawId) === String(sectionMasterUuid)) {
  //                         return false;
  //                     }
  //                 }

  //                 // Only include rows with the same section and broadhead
  //                 const sec = String(it.section ?? it.sectionCode ?? '').trim().toLowerCase();
  //                 const bh = String(it.broadHead ?? '').trim().toLowerCase();
  //                 return sec === secVal && bh === bhVal;
  //             });
  //             const newStart = start;
  //             const newEnd = end || new Date(9999, 11, 31);
  //             const overlap = candidates.some((it) => {

  //                 const exStart = parseDate(it.startDate);
  //                 const exEnd = it.endDate ? parseDate(it.endDate) : new Date(9999, 11, 31);
  //                 if (!exStart) return false;
  //                 return newStart <= exEnd && exStart <= newEnd;
  //             });

  //             if (overlap) {
  //                 this.showNotification(context, 'Start Date and End Date are overlapping for same section and broadhead', 'error');
  //                 return;
  //             }
  //             // Gap confirmation: if previous record ends before new start - 1 day, prompt
  //             const prevEnds = candidates
  //                 .map(it => parseDate(it.endDate))
  //                 .filter(d => d instanceof Date && !isNaN(d));
  //             if (prevEnds.length > 0) {
  //                 const before = prevEnds.filter(d => d < newStart).sort((a, b) => b - a);
  //                 if (before.length > 0) {
  //                     const prevEnd = before[0];
  //                     const expected = new Date(prevEnd.getFullYear(), prevEnd.getMonth(), prevEnd.getDate() + 1);
  //                     const sameDay = newStart.getFullYear() === expected.getFullYear() && newStart.getMonth() === expected.getMonth() && newStart.getDate() === expected.getDate();
  //                     if (!sameDay) {
  //                         if (typeof context?.openGapConfirm === 'function') {
  //                             context.openGapConfirm(
  //                                 'There is a gap between the "End Date" & "Start Date" for which there will be no applicability of section & broadhead. Do you want to continue?',
  //                                 async () => {
  //                                     await this._saveSectionMaster(rowData, { ...context, toDD });
  //                                 }
  //                             );
  //                             return; // wait for user confirmation
  //                         }
  //                     }
  //                 }
  //             }
  //         }

  //         // 2. endDate should not be before startDate
  //         if (start && end && end < start) {
  //             this.showNotification(context, 'End Date cannot be before Start Date.', 'error');
  //             return;
  //         }

  //         // 3. endDate and startDate cannot be same
  //         if (start && end && end.getTime() === start.getTime()) {
  //             this.showNotification(context, 'Start Date and End Date cannot be the same.', 'error');
  //             return;
  //         }

  //         // 4. Numeric validations
  //         const num18_2 = /^\d{1,18}(\.\d{1,2})?$/; // up to 18 digits, optional .dd
  //         const num3_2 = /^\d{1,3}(\.\d{1,2})?$/;  // up to 3 digits, optional .dd

  //         if (!isEmpty(rowData.thresholdAnnual) && !num18_2.test(String(rowData.thresholdAnnual).trim())) {
  //             this.showNotification(context, 'Threshold Limit (Annual) must be numeric up to 18 digits with up to 2 decimals.', 'error');
  //             return;
  //         }
  //         if (!isEmpty(rowData.thresholdTransaction) && !num18_2.test(String(rowData.thresholdTransaction).trim())) {
  //             this.showNotification(context, 'Threshold Limit (Per transaction) must be numeric up to 18 digits with up to 2 decimals.', 'error');
  //             return;
  //         }
  //         if (!isEmpty(rowData.prescribedRate) && !num3_2.test(String(rowData.prescribedRate).trim())) {
  //             this.showNotification(context, 'Prescribed Rate must be numeric up to 3 digits with up to 2 decimals.', 'error');
  //             return;
  //         }

  //         // Build payload with normalized dates
  //         const payload = {
  //             sectionCode: rowData.section || '',
  //             broadHead: rowData.broadHead || '',
  //             startDate: toDD(rowData.startDate) || '',
  //             endDate: rowData.endDate ? toDD(rowData.endDate) : '',
  //             applicableTo: rowData.applicableTo || '',
  //             payeeStatus: rowData.payeeStatus || '',
  //             thresholdAnnual: rowData.thresholdAnnual || '',
  //             thresholdTransaction: rowData.thresholdTransaction || '',
  //             prescribedRate: rowData.prescribedRate || '',
  //             globalSectionMaster,
  //             associateSectionMaster,
  //             tanUuid
  //         };

  //         await this._saveSectionMaster({
  //             ...rowData,
  //             sectionMasterUuid,
  //         }, { ...context, toDD }, payload);

  //         // Refresh table data only after successful save
  //         const searchRes = await searchSectionMasterApi({
  //             pageFilter: {
  //                 currentPage: 1,
  //                 includeCount: true,
  //                 pageSize: 100,
  //                 sortField: 'sectionCode',
  //                 sortOrder: 'DESC',
  //             },
  //             requestFilter: [],
  //             isReportDownload: false,
  //             searchCOList: [],
  //             hitType: 'GET_SECTION_MASTER'
  //         }, tanUuid);
  //         const apiData = searchRes?.data;
  //         const list =
  //             apiData?.data?.list ||
  //             apiData?.list ||
  //             apiData?.data?.content ||
  //             apiData?.content ||
  //             [];
  //         const mapRows = (items) =>
  //             Array.isArray(items)
  //                 ? items.map((created, i) => {
  //                     const mapped = {
  //                         section: created.section ?? created.sectionCode ?? '',
  //                         broadHead: created.broadHead ?? '',
  //                         startDate: created.startDate ? created.startDate : '',
  //                         endDate: created.endDate ? created.endDate : '',  // ✅ Preserve API value
  //                         applicableTo: created.applicableTo ?? '',
  //                         payeeStatus: created.payeeStatus ?? '',
  //                         thresholdAnnual: created.thresholdAnnual ?? '',
  //                         thresholdTransaction: created.thresholdTransaction ?? '',
  //                         prescribedRate: created.prescribedRate ?? '',
  //                         id: created.id ?? i, // make sure every row has an id
  //                     }

  //                     return mapped
  //                 })
  //                 : [];
  //         if (context?.setTableData) context.setTableData(mapRows(list));
  //         if (context?.setRawSectionMasterList) context.setRawSectionMasterList(mapRows(list))

  //         this.showNotification(context, 'Section updated successfully!', 'success');
  //         if (context?.setSelectedRows) context.setSelectedRows([]);

  //         // Reset editing state only after successful save
  //         if (context?.setEditingRowId) {
  //             context.setEditingRowId(null);
  //         }
  //         if (context?.setEditingData) {
  //             context.setEditingData({});
  //         }

  //     } catch (e) {
  //         this.showNotification(context, e.message || 'Failed to update section', 'error');

  //         // Clear all editing state on error
  //         if (context?.setEditingRowId) context.setEditingRowId(null);
  //         if (context?.setEditingData) context.setEditingData({});
  //         if (context?.setSelectedRows) context.setSelectedRows([]);
  //         if (context?.setValidationErrors) context.setValidationErrors({});

  //         try {
  //             const tanUuid = context?.selectedTanMasterUuid || '';
  //             const searchRes = await searchSectionMasterApi({
  //                 pageFilter: {
  //                     currentPage: 1,
  //                     includeCount: true,
  //                     pageSize: 100,
  //                     sortField: 'sectionCode',
  //                     sortOrder: 'DESC',
  //                 },
  //                 requestFilter: [],
  //                 isReportDownload: false,
  //                 searchCOList: [],
  //                 hitType: 'GET_SECTION_MASTER'
  //             }, tanUuid);
  //             const apiData = searchRes?.data;
  //             const list =
  //                 apiData?.data?.list ||
  //                 apiData?.list ||
  //                 apiData?.data?.content ||
  //                 apiData?.content ||
  //                 [];
  //             const mapRows = (items) =>
  //                 Array.isArray(items)
  //                     ? items.map((created, i) => {
  //                         const mapped = {
  //                             section: created.section ?? created.sectionCode ?? '',
  //                             broadHead: created.broadHead ?? '',
  //                             startDate: created.startDate ? created.startDate : '',
  //                             endDate: created.endDate ? created.endDate : '',
  //                             applicableTo: created.applicableTo ?? '',
  //                             payeeStatus: created.payeeStatus ?? '',
  //                             thresholdAnnual: created.thresholdAnnual ?? '',
  //                             thresholdTransaction: created.thresholdTransaction ?? '',
  //                             prescribedRate: created.prescribedRate ?? '',
  //                             id: created.id ?? i, // make sure every row has an id
  //                         }

  //                         return mapped
  //                     })
  //                     : [];
  //             if (context?.setTableData) context.setTableData(mapRows(list));
  //             if (context?.setRawSectionMasterList) context.setRawSectionMasterList(mapRows(list))
  //         } catch (refreshError) {
  //             this.showNotification(context, 'Failed to refresh data after save error', 'error');
  //         }
  //     }
  // };

  handleSectionMasterSaveEdit = async (rowData, context) => {  
      
    try {
      const rawList = context?.rawSectionMasterList || []
      const sectionMasterUuid = rowData.sectionMasterUuid
      const tanUuid = context?.selectedTanMasterUuid || ''

      /** ---------------- Helpers ---------------- */
      const parseDate = s => {
        if (!s) return null
        const t = String(s).trim()
        if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
          const [y, m, d] = t.split('-').map(Number)
          return new Date(y, m - 1, d)
        }
        if (/^\d{2}[\/. -]\d{2}[\/. -]\d{4}$/.test(t)) {
          const parts = t.split(/[\/. -]/).map(Number)
          const [d, m, y] = parts
          return new Date(y, m - 1, d)
        }
        const dt = new Date(t)
        return isNaN(dt.getTime()) ? null : dt
      }

      const toDD = s => {
        const t = (s || '').toString().trim()
        if (!t || t === '-') return ''
        if (/^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(t)) return t.replace(/-/g, '/')
        if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
          const [y, m, d] = t.split('-')
          return `${d}/${m}/${y}`
        }
        return t
      }

     const isEmpty = val =>
      val === null || val === undefined || String(val).trim() === ''

      /** ---------------- Date Validations ---------------- */
      const start = parseDate(rowData.startDate)
      const end = rowData.endDate ? parseDate(rowData.endDate) : null

      if (!start) {
        this.showNotification(
          context,
          'Start Date is required and must be valid.',
          'error'
        )
        return
      }

      if (end && end <= start) {
        this.showNotification(
          context,
          'End Date must be greater than Start Date.',
          'error'
        )
        return
      }

      if (end) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        if (end <= today) {
          this.showNotification(
            context,
            'End Date should only be in future dates.',
            'error'
          )
          return
        }
      }

      /** ---------------- Broad Head Validation ---------------- */
      if (isEmpty(rowData.broadHead)) {
        const broad = rowData.broadHead.trim()
        if (broad.length > 1000) {
          this.showNotification(
            context,
            'Broad Head must not exceed 1000 characters.',
            'error'
          )
          return
        }
      }

      if (isEmpty(rowData.payeeStatus)) {
          this.showNotification(
            context,
            'Payee status is required.',
            'error'
          )
          return
      }

      /** ---------------- Numeric Validations ---------------- */
      const num18_2 = /^\d{1,18}(\.\d{1,2})?$/
      const num3_2 = /^\d{1,3}(\.\d{1,2})?$/

      if (
        isEmpty(rowData.thresholdAnnual)
      ) {
        this.showNotification(
          context,
          'Threshold Annual cannot be empty.',
          'error'
        )
        return
      } else if(!num18_2.test(String(rowData.thresholdAnnual).trim())){
         this.showNotification(
          context,
          'Threshold Annual must be up to 18 digits with 2 decimals.',
          'error'
        )
        return
      }
      if (
        isEmpty(rowData.thresholdTransaction)
      ) {
        this.showNotification(
          context,
          'Threshold Transaction cannot be empty.',
          'error'
        )
        return
      } else if(!num18_2.test(String(rowData.thresholdTransaction).trim())){
         this.showNotification(
          context,
          'Threshold Transaction must be up to 18 digits with 2 decimals.',
          'error'
        )
      }
      if (
        isEmpty(rowData.prescribedRate) 
      ) {
        this.showNotification(
          context,
          'Prescribed Rate cannot be empty.',
          'error'
        )
        return
      } else if(!num3_2.test(String(rowData.prescribedRate).trim())){
        this.showNotification(
          context,
          'Prescribed Rate must be up to 3 digits with 2 decimals.',
          'error'
        )
        return
      }

      /** ---------------- Gap Validation ---------------- */
      const secVal = String(rowData.section || '')
        .trim()
        .toLowerCase()
      const bhVal = String(rowData.broadHead || '')
        .trim()
        .toLowerCase()
      if (secVal && bhVal && start) {
        const candidates = rawList.filter(it => {
          const rawId = it.uuid || it.id || it.sectionMasterUuid
          const currentId = rowData.sectionMasterUuid
          if (rawId && currentId && String(rawId) === String(currentId))
            return false
          const sec = String(it.section ?? it.sectionCode ?? '')
            .trim()
            .toLowerCase()
          const bh = String(it.broadHead ?? '')
            .trim()
            .toLowerCase()
          return sec === secVal && bh === bhVal
        })

        const prevEnds = candidates
          .map(it => parseDate(it.endDate))
          .filter(d => d instanceof Date && !isNaN(d))
        if (prevEnds.length > 0) {
          const before = prevEnds.filter(d => d < start).sort((a, b) => b - a)
          if (before.length > 0) {
            const prevEnd = before[0]
            const expected = new Date(
              prevEnd.getFullYear(),
              prevEnd.getMonth(),
              prevEnd.getDate() + 1
            )
            const sameDay =
              start.getFullYear() === expected.getFullYear() &&
              start.getMonth() === expected.getMonth() &&
              start.getDate() === expected.getDate()
            if (!sameDay && typeof context?.openGapConfirm === 'function') {
              context.openGapConfirm(
                'There is a gap between the "End Date" & "Start Date" for which there will be no applicability of section & broadhead. Do you want to continue?',
                async () => {
                  await this.handleSectionMasterSaveEdit(rowData, context)
                }
              )
              return // wait for user confirmation
            }
          }
        }
      }

      // 🔹 Overlap Validation
      if (start) {
        const secVal = String(rowData.section || '')
          .trim()
          .toLowerCase()
        const bhVal = rowData.broadHead.toLowerCase()

        const candidates = (Array.isArray(rawList) ? rawList : []).filter(it => {
          const rawId = it.sectionMasterUuid || it.uuid || it.id
          const currentId = rowData.sectionMasterUuid
          if (rawId && currentId && String(rawId) === String(currentId))
            return false
          const sec = String(it.section ?? it.sectionCode ?? '')
            .trim()
            .toLowerCase()
          const bh = String(it.broadHead ?? '')
            .trim()
            .toLowerCase()
          return sec === secVal && bh === bhVal
        })

        const newStart = start
        const newEnd = end || new Date(9999, 11, 31)

        // Overlap check
        const overlap = candidates.some(it => {
          const exStart = parseDate(it.startDate)
          const exEnd = it.endDate
            ? parseDate(it.endDate)
            : new Date(9999, 11, 31)
          if (!exStart) return false
          return newStart <= exEnd && exStart <= newEnd
        })
        if (overlap) {
          this.showNotification(
            context,
            'Overlapping date range with existing record.',
            'error'
          )
          return
        }
      }

      /** ---------------- Build Payload ---------------- */
      const payload = {
        sectionCode: rowData.section || '',
        broadHead: rowData.broadHead || '',
        startDate: toDD(rowData.startDate) || '',
        endDate: rowData.endDate ? toDD(rowData.endDate) : '',
        applicableTo: rowData.applicableTo || '',
        payeeStatus: rowData.payeeStatus || '',
        thresholdAnnual: rowData.thresholdAnnual || 0,
        thresholdTransaction: rowData.thresholdTransaction || 0,
        prescribedRate: rowData.prescribedRate || 0,
        globalSectionMaster:rowData.globalSectionMaster,
        associateSectionMaster:rowData?.associateSectionMaster,
        tanUuid,
      }

      /** ---------------- Save ---------------- */
      await this._saveSectionMaster(
        { ...rowData, sectionMasterUuid },
        { ...context, toDD },
        payload
      )

      /** ---------------- Refresh Table ---------------- */
      const searchRes = await searchSectionMasterApi(
        {
          pageFilter: {
            currentPage: 1,
            includeCount: true,
            pageSize: 100,
            sortField: 'sectionCode',
            sortOrder: 'ASC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: [],
          hitType: 'GET_SECTION_MASTER',
        },
        tanUuid
      )

      const apiData = searchRes?.data
      
      const list = apiData?.content || []
      context.setRawSectionMasterList(list)
      const mapRows = items =>
        Array.isArray(items)
          ? items.map((created, i) => ({
              section: created.section ?? created.sectionCode ?? '',
              broadHead: created.broadHead ?? '',
              startDate: created.startDate || '',
              endDate: created.endDate || '',
              applicableTo: created.applicableTo ?? '',
              payeeStatus: created.payeeStatus ?? '',
              thresholdAnnual: created.thresholdAnnual ?? '',
              thresholdTransaction: created.thresholdTransaction ?? '',
              prescribedRate: created.prescribedRate ?? '',
              sectionMasterUuid: created.uuid,
              globalSectionMaster:created.globalSectionMaster,
              associateSectionMaster: created.associateSectionMaster,
            }))
          : []

      if (context?.setTableData) context.setTableData(mapRows(list))
      if (context?.setRawSectionMasterList) context.setRawSectionMasterList(mapRows(list))
      if (context?.setSelectedRows) context.setSelectedRows([])
      if (context?.setEditingData) context.setEditingData({})
      if (context?.setEditingRowId) context.setEditingRowId(null)
      if (context?.setValidationErrors) context.setValidationErrors({})
      this.showNotification(context, 'Section updated successfully!', 'success')
    } catch (e) {
      this.showNotification(
        context,
        e.message || 'Failed to update section',
        'error'
      )
    }
  }

  handleSectionMasterCancelEdit = (data, context) => {
    if (context?.setEditingRowId) {
      context.setEditingRowId(null)
    }
    if (context?.setEditingData) {
      context.setEditingData({})
    }
  }

  handleSectionMasterSearch = (data, context) => {
    // Open the search dialog if handler is provided in context
    if (context?.openSearchDialog) {
      context.openSearchDialog()
    } else {
      this.showNotification(context, 'Search dialog not available', 'error')
    }
  }

  handleSectionMasterEditFieldChange = (rowId, field, value, context) => {
    if (context?.setEditingData) {
      context.setEditingData(prev => ({
        ...prev,
        [rowId]: {
          ...(prev[rowId] || {}),
          [field]: value,
        },
      }))
    }
  }

  // Rule Setting specific handlers
  handleRuleSettingAdd = (data, context) => {
    if (context?.showAddRuleSetting) {
      context.showAddRuleSetting(true)
    } else {
      this.showNotification(
        context,
        'Add Rule functionality will be implemented soon',
        'info'
      )
    }
  }

  // Section Keyword specific handlers
  handleSectionKeywordAdd = (data, context) => {
    if (context?.showAddKeywordDialog) {
      context.showAddKeywordDialog(true)
    } else {
      this.showNotification(
        context,
        'Add new section keyword functionality',
        'info'
      )
    }
  }

  /**
   * Get action handlers for a specific component
   * @param {string} componentType - The type of component (sectionMaster, vendorDetails, etc.)
   * @param {Object} context - Additional context data (selectedRows, tableData, etc.)
   * @returns {Object} Action handlers object
   */
  getActionHandlers(componentType, context = {}) {
    const handlers = this.actionHandlers[componentType]
    if (!handlers) {
      console.warn(
        `No action handlers found for component type: ${componentType}`
      )
      return {}
    }

    // Bind context to handlers
    const boundHandlers = {}
    Object.keys(handlers).forEach(actionKey => {
      boundHandlers[actionKey] = (...args) =>
        handlers[actionKey](...args, context)
    })

    return boundHandlers
  }

  /**
   * Get action icons configuration for ReusableTable
   * @param {string} componentType - The type of component
   * @param {Object} context - Additional context data (should include selectedRows)
   * @returns {Array} Array of action objects with icons and handlers in custom order
   */
  getTableActions(componentType, context = {}) {
    const handlers = this.getActionHandlers(componentType, context)
    const config = this.componentConfigs[componentType]
    if (!config) {
      console.warn(
        `No configuration found for component type: ${componentType}`
      )
      return []
    }

    const order = config.iconOrder
    const actions = []
    const selectedRows = context.selectedRows || []
    const hasSelectedRows = selectedRows.length > 0

    // Build actions array in the specified order
    order.forEach(actionKey => {
      // Special handling for monthlyToggle - renders StyledSwitch
      if (actionKey === 'monthlyToggle' && this.actionConfigs[actionKey]) {
        const actionConfig = { ...this.actionConfigs[actionKey] }
        actionConfig.icon = (
          <StyledSwitch
            size="small"
            checked={!!context.isMonthlyView}
            onChange={(e, checked) => { if (context.monthlyToggleHandler) context.monthlyToggleHandler(checked) }}
          />
        )
        actionConfig.tooltip = 'Monthly View'
        actions.push(actionConfig)
        return
      }

      if (handlers[actionKey] && this.actionConfigs[actionKey]) {
        const actionConfig = { ...this.actionConfigs[actionKey] }

        // Always show search icon in header, and wire to openSearchDialog
        if (actionKey === 'search' && componentType === 'sectionMaster') {
          // Attach ref and onClick for correct popper alignment
          actionConfig.buttonProps = {
            ref: context.searchIconRef || null,
            onClick: () => {
              if (context.openSearchDialog) {
                context.openSearchDialog()
              } else {
                this.showNotification(
                  context,
                  'Search dialog not available',
                  'error'
                )
              }
            },
          }
          actions.push(actionConfig)
          return
        }

        // Special handling for delete action - disable if no rows selected
        if (actionKey === 'dlt') {
          actionConfig.disabled = !hasSelectedRows
          actionConfig.onClick = hasSelectedRows
            ? handlers[actionKey]
            : () => {
                this.showNotification(
                  context,
                  'Please select at least one row to delete',
                  'warning'
                )
              }
          // Change color when disabled
          if (!hasSelectedRows) {
            actionConfig.color = '#cccccc'
            actionConfig.tooltip = 'Select rows to enable delete'
          } else {
            actionConfig.tooltip = `Delete ${selectedRows.length} selected row(s)`
          }
        } else if (actionKey === 'edit' && componentType === 'sectionMaster') {
          // Special handling for edit action - change icon to save when editing
          const isEditing = context.editingRowId !== null
          if (isEditing) {
            actionConfig.icon = <SaveIcon />
            actionConfig.tooltip = 'Save Changes'
            actionConfig.color = 'var(--forbrand-primary)'
            // Save icon: handle table-wide vs single-row save
            actionConfig.onClick = row => {
              const tableData = context.tableData || []
              const editingData = context.editingData || {}
              const currentEditing = context.editingRowId

              // Table-wide edit mode: perform bulk update using pending edits
              if (currentEditing === '__ALL__') {
                this.handleSectionMasterBulkUpdate(context)
                return
              }

              // Single-row edit mode: merge and save that row
              const rowId = row?.sectionMasterUuid ?? currentEditing
              if (rowId === null || rowId === undefined) {
                this.showNotification(
                  context,
                  'No row is being edited.',
                  'error'
                )
                return
              }
              const originalRow = tableData.find(
                r => r.sectionMasterUuid === rowId
              )
              if (!originalRow) {
                this.showNotification(
                  context,
                  'Could not find the row to save.',
                  'error'
                )
                return
              }
              const mergedRow = {
                ...originalRow,
                ...(editingData[rowId] || {}),
              }
              handlers[actionKey](mergedRow)
            }
          } else {
            // Edit icon: just call handler with rowData (should be handled in table row context)
            actionConfig.onClick = handlers[actionKey]
          }
        } else {
          actionConfig.onClick = handlers[actionKey]
        }

        actions.push(actionConfig)
      }
    })

    return actions
  }

  /**
   * Get action icons configuration for TabbedTableView
   * @param {string} componentType - The type of component
   * @param {Object} context - Additional context data
   * @returns {Object} Action icons object with handlers
   */
  getTabbedViewActions(componentType, context = {}) {
    return this.getActionHandlers(componentType, context)
  }

  /**
   * Add a new component configuration
   * @param {string} componentType - The component type identifier
   * @param {Object} config - The component configuration
   */
  addComponentConfig(componentType, config) {
    this.componentConfigs[componentType] = config
    this.actionHandlers = this.generateActionHandlers()
  }

  /**
   * Update icon order for a component
   * @param {string} componentType - The component type
   * @param {Array} newOrder - New icon order array
   */
  updateIconOrder(componentType, newOrder) {
    if (this.componentConfigs[componentType]) {
      this.componentConfigs[componentType].iconOrder = newOrder
    }
  }

  /**
   * Get available component types
   * @returns {Array} Array of component type strings
   */
  getAvailableComponents() {
    return Object.keys(this.componentConfigs)
  }

  /**
   * Get component configuration
   * @param {string} componentType - The component type
   * @returns {Object} Component configuration
   */
  getComponentConfig(componentType) {
    return this.componentConfigs[componentType]
  }
}

// Export the ActionManager class
export default ActionManager
